import uuidv4 from 'uuid/v4';
import { isChoiceStep } from './stepUtils';
import { Combinator, EqualityOperator, } from './types';
export function addChoice(mutable, emptyChoiceDefinition) {
    const choiceUUID = uuidv4();
    mutable.choices.push(choiceUUID);
    mutable.choiceDefinitions[choiceUUID] = emptyChoiceDefinition;
    return choiceUUID;
}
export function hasAChoiceOption(mutable) {
    // some choices on multiple choice step might be null if the user
    // has not provided a value for them in the builder
    return Object.values(mutable.choiceDefinitions).some((value) => value !== null);
}
export function removeChoice(mutable, uuid) {
    delete mutable.choiceDefinitions[uuid];
    const choiceToRemoveIndex = mutable.choices.indexOf(uuid);
    mutable.choices.splice(choiceToRemoveIndex, 1);
}
export function reorderChoices(mutable, oldIndex, newIndex) {
    const [removed] = mutable.choices.splice(oldIndex, 1);
    mutable.choices.splice(newIndex, 0, removed);
}
export function getChoices(mutable) {
    return mutable.choices;
}
export function getChoicesByStepUUID(userTest, stepUUID) {
    const step = userTest.stepDefinitions[stepUUID];
    if (!step || !isChoiceStep(step))
        return [];
    return step.choices;
}
export function getSelectionRange(mutable) {
    return mutable.selectionRange || [null, null];
}
export function setSelectionRange(mutable, range) {
    mutable.selectionRange = range;
}
export function setMultipleSelectionMode(mutable, value) {
    mutable.multipleSelectionMode = value;
}
export function setMultipleSelection(mutable, value, range = [null, null]) {
    setMultipleSelectionMode(mutable, value);
    // null represents a lack of value until its set in the builder
    // this is needed to render the placeholders in the inputs
    setSelectionRange(mutable, range);
}
export function removeMultipleSelection(mutable) {
    delete mutable.multipleSelectionMode;
    setSelectionRange(mutable, [1, 1]);
}
export function getMultipleSelectionMode(mutable) {
    return mutable.multipleSelectionMode || null;
}
export function isMultipleSelectionEnabled(mutable) {
    const multipleSelectionMode = getMultipleSelectionMode(mutable);
    return multipleSelectionMode !== null;
}
export function getIsRandomizeSet(mutable) {
    return !!mutable.randomize;
}
export function setRandomizeSetting(mutable, randomize) {
    mutable.randomize = randomize;
}
export function setCombinator({ mutable, ruleIndex, requirementIndex, combinator, }) {
    if (mutable.conditions)
        mutable.conditions.rules[ruleIndex].requirements[requirementIndex].combinator = combinator;
}
export function getConditionalNextStepUUID(mutable, answerUUIDs) {
    var _a, _b, _c, _d;
    const evaluatedData = ((_a = mutable.conditions) === null || _a === void 0 ? void 0 : _a.rules)
        ? evaluateRules((_b = mutable.conditions) === null || _b === void 0 ? void 0 : _b.rules)
        : [];
    if (answerUUIDs.length) {
        const ruleMatch = evaluatedData.find(({ allow, exclude }) => {
            const hasAllAllowAnswers = allow.every((ruleRequirement) => answerUUIDs.find((answer) => answer === ruleRequirement));
            const hasExcludedAnswers = exclude.some((ruleRequirement) => answerUUIDs.find((answer) => answer === ruleRequirement));
            return hasAllAllowAnswers && !hasExcludedAnswers;
        });
        if (ruleMatch === null || ruleMatch === void 0 ? void 0 : ruleMatch.destinationUUID) {
            return ruleMatch === null || ruleMatch === void 0 ? void 0 : ruleMatch.destinationUUID;
        }
    }
    // if no match to rules, check the Always condition
    return (_d = (_c = mutable.conditions) === null || _c === void 0 ? void 0 : _c.ALWAYS) !== null && _d !== void 0 ? _d : null;
}
function evaluateRules(rules) {
    let evalRules = [];
    rules.forEach(({ destinationStepUUID, requirements }) => {
        const initalValue = {
            allow: [],
            exclude: [],
            destinationUUID: destinationStepUUID,
        };
        const requirementRules = requirements.reduce((previousValue, currentValue) => {
            const { operator, selectedChoiceUUID, combinator } = currentValue;
            if (combinator === Combinator.Or) {
                const newEvalRule = {
                    allow: [],
                    exclude: [],
                    destinationUUID: destinationStepUUID,
                };
                sortByEqualityOperator(operator, newEvalRule, selectedChoiceUUID);
                previousValue.push(newEvalRule);
            }
            else {
                sortByEqualityOperator(operator, previousValue[previousValue.length - 1], selectedChoiceUUID);
            }
            return previousValue;
        }, [initalValue]);
        evalRules = [...evalRules, ...requirementRules];
    });
    return evalRules;
}
function sortByEqualityOperator(operator, evalRule, selectedChoiceUUID) {
    if (operator === EqualityOperator.IsNot) {
        evalRule.exclude.push(selectedChoiceUUID);
    }
    else {
        evalRule.allow.push(selectedChoiceUUID);
    }
}
export function setConditionalRule(mutable, selectedChoiceUUID, selectedStepUUID, selectedOperator, selectedCombinator) {
    if (mutable.conditions) {
        mutable.conditions.rules.push({
            destinationStepUUID: selectedStepUUID,
            requirements: [
                {
                    operator: selectedOperator !== null && selectedOperator !== void 0 ? selectedOperator : EqualityOperator.Is,
                    selectedChoiceUUID,
                    combinator: selectedCombinator,
                },
            ],
        });
    }
    // support for documents that don't have this conditions object yet
    else {
        mutable.conditions = {
            rules: [
                {
                    destinationStepUUID: selectedStepUUID,
                    requirements: [
                        {
                            operator: EqualityOperator.Is,
                            selectedChoiceUUID,
                            combinator: Combinator.And,
                        },
                    ],
                },
            ],
            ALWAYS: null,
        };
    }
}
export function setAlwaysConditionalRule(mutable, selectedStepUUID) {
    if (mutable.conditions) {
        mutable.conditions.ALWAYS = selectedStepUUID;
    }
    else {
        mutable.conditions = {
            rules: [],
            ALWAYS: selectedStepUUID,
        };
    }
}
export function deleteConditionalRule(mutable, index) {
    if (mutable.conditions) {
        mutable.conditions.rules.splice(index, 1);
    }
}
export function deleteAlwaysConditionalRule(mutable) {
    if (mutable.conditions) {
        mutable.conditions.ALWAYS = null;
    }
    else {
        mutable.conditions = {
            rules: [],
            ALWAYS: null,
        };
    }
}
export function updateAnswerValue({ mutable, ruleIndex, answer, }) {
    if (mutable.conditions) {
        mutable.conditions.rules[ruleIndex].requirements[0].selectedChoiceUUID =
            answer;
    }
    else {
        mutable.conditions = {
            rules: [],
            ALWAYS: null,
        };
    }
}
export function updateDestinationStepUUID({ mutable, ruleIndex, stepUUID, }) {
    if (mutable.conditions) {
        mutable.conditions.rules[ruleIndex].destinationStepUUID = stepUUID;
    }
    else {
        addEmptyRulesObject(mutable);
    }
}
export function getRequirementsList({ mutable, ruleIndex, }) {
    var _a;
    if (mutable.conditions) {
        return (_a = mutable.conditions.rules[ruleIndex].requirements) !== null && _a !== void 0 ? _a : null;
    }
    return null;
}
export function getRequirement({ mutable, ruleIndex, requirementIndex, }) {
    var _a;
    if (mutable.conditions) {
        return ((_a = mutable.conditions.rules[ruleIndex].requirements[requirementIndex]) !== null && _a !== void 0 ? _a : null);
    }
    return null;
}
export function setRequirement({ mutable, ruleIndex, selectedChoiceUUID, operator = EqualityOperator.Is, combinator = Combinator.And, }) {
    var _a;
    if (mutable.conditions) {
        (_a = mutable.conditions) === null || _a === void 0 ? void 0 : _a.rules[ruleIndex].requirements.push({
            operator,
            selectedChoiceUUID,
            combinator,
        });
    }
}
export function getRule({ mutable, ruleIndex, }) {
    var _a;
    if (mutable.conditions) {
        return (_a = mutable.conditions.rules[ruleIndex]) !== null && _a !== void 0 ? _a : null;
    }
    return null;
}
export function updateEqualityOperator({ mutable, ruleIndex, equalityOperatorValue, requirementIndex, }) {
    if (mutable.conditions) {
        mutable.conditions.rules[ruleIndex].requirements[requirementIndex].operator = equalityOperatorValue;
    }
    else {
        addEmptyRulesObject(mutable);
    }
}
export function deleteRequirement(mutable, // TODO move this into conditional logic utils
ruleIndex, requirementIndex) {
    var _a;
    if (mutable.conditions) {
        // if this is the last requirement, delete the entire rule
        if (mutable.conditions.rules[ruleIndex].requirements.length <= 1) {
            deleteConditionalRule(mutable, ruleIndex);
        }
        else {
            (_a = mutable.conditions) === null || _a === void 0 ? void 0 : _a.rules[ruleIndex].requirements.splice(requirementIndex, 1);
        }
    }
}
export function addEmptyRulesObject(mutable, selectedStepUUID, selectedChoiceUUID) {
    mutable.conditions = {
        rules: selectedStepUUID
            ? [
                {
                    destinationStepUUID: selectedStepUUID,
                    requirements: selectedChoiceUUID
                        ? [
                            {
                                operator: EqualityOperator.Is,
                                selectedChoiceUUID,
                                combinator: Combinator.And,
                            },
                        ]
                        : [],
                },
            ]
            : [],
        ALWAYS: null,
    };
}
